import { DOCUMENT } from "@angular/common";
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, interval, Observable, of, Subscription } from "rxjs";
import {
    debounce,
    distinctUntilChanged,
    filter,
    first,
    map,
    switchMap,
} from "rxjs/operators";

import { CtaSize } from "@hermes/aphrodite/call-to-action";
import { SearchInputComponent } from "@hermes/aphrodite/fields";
import { Context, LOCALE } from "@hermes/app-core";
import { isFrance, Locale } from "@hermes/locale";
import { Logger } from "@hermes/logger";
import {
    AutocompletePrediction,
    AutoCompleteProviderService,
    GeoService,
    isBaiduEngine,
    LoadGeoLibrariesService,
} from "@hermes/map-utils";
import { FormService } from "@hermes/utils-generic/services/user-interface";
import { LOGGER } from "@hermes/web-logger";

import { SelectedLocation } from "./places-search-bar.type";

/**
 * This component implements a global search location form.
 *
 * It displays the search input, the drop-down list and the submit button.
 *
 * Usage:
 * ```
 *  <h-places-search-bar
 *      [isWorldwideResearch]="true"
 *      [ctaSize]="'small'"
 *      (geolocationButtonSelected)="geolocationButtonSelected($event)"
 *      (locationSelected)="locationSelected($event)">
 *  </h-places-search-bar>
 * ```
 */
@Component({
    selector: "h-places-search-bar",
    templateUrl: "./places-search-bar.component.html",
    styleUrls: ["./places-search-bar.component.scss"],
    standalone: false,
})
export class PlacesSearchBarComponent
    implements OnInit, OnDestroy, AfterViewInit
{
    /**
     * Event fired when a new location has been selected by the user
     */
    @Output()
    public locationSelected = new EventEmitter<SelectedLocation>();

    /**
     * Event fired when the geolocation button is selected
     */
    @Output()
    public geolocationButtonSelected = new EventEmitter<string>();

    @Input()
    public isWorldwideResearch = false;

    @Input()
    public forceInlineDisplay = false;

    @Input()
    public ctaSize: CtaSize | undefined = undefined;

    @ViewChild(SearchInputComponent)
    public searchInputComponent!: SearchInputComponent;

    @ViewChild("searchLocation")
    public searchLocation!: HTMLFormElement;

    /**
     * FormControl used by the generic search-input component.
     */
    public searchInputControl = new FormControl("");

    /**
     * FormGroup used to associate `searchInput` model with view. FormGroup tracks the status and changes of its controls.
     */
    public searchLocationForm = new FormGroup({
        searchInput: this.searchInputControl,
    });

    /**
     * Use in template to personalize an error message according the country (need of $localize)
     */
    public unknownPlaceMessage: string = "";

    /**
     * This Observable manages the events after the user has entered a new value in the search field :
     * filtering, control, call the Autocomplete service.
     */
    public suggestions$ = new BehaviorSubject<AutocompletePrediction[]>([]);

    public hasSuggestions$ = this.suggestions$.pipe(
        map((suggestion) => suggestion.length > 0),
    );
    /**
     * This Observable is used to display clear button
     */
    public showClearButton$: Observable<boolean>;

    /**
     * This Observable is used to display geolocalization button
     */
    public showGeolocalization$: Observable<boolean>;

    public isBaiduEngine: boolean = isBaiduEngine(this.locale.countryCode);

    public showSuggestions$: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(true);

    public geolocationError: Error | undefined;

    public countryError: boolean = false;

    public position: number | undefined;

    public readonly searchInputId = "search-location";

    private subscription = new Subscription();

    private bMapAutocomplete: BMap.Autocomplete | undefined;

    constructor(
        private autoCompleteProvider: AutoCompleteProviderService,
        private context: Context,
        private geoService: GeoService,
        private changeDetectorRef: ChangeDetectorRef,
        private formService: FormService,
        private loadGeoLibrariesService: LoadGeoLibrariesService,
        private translateService: TranslateService,
        private ngZone: NgZone,
        @Inject(LOGGER) private logger: Logger,
        @Inject(DOCUMENT) private document: Document,
        @Inject(LOCALE) private locale: Locale,
    ) {
        /**
         * We should see the delete button only if there is data in the entry.
         * On the other hand, we have to see the geolocation button if the input is empty.
         */
        this.showClearButton$ = this.searchInputControl.valueChanges.pipe(
            map(Boolean),
        );

        this.showGeolocalization$ = this.showClearButton$.pipe(
            map((value) => !value),
        );
    }

    public ngOnInit(): void {
        this.unknownPlaceMessageManagement();
        this.statusManagement();

        if (this.context.isInBrowserMode()) {
            this.searchInputChangesManagement();
        }
    }

    public ngAfterViewInit(): void {
        if (this.isBaiduEngine) {
            this.subscription.add(
                this.loadGeoLibrariesService.isLoaded$
                    .pipe(
                        filter((status) => status),
                        first(),
                    )
                    .subscribe(() => {
                        this.initBaiduAutocomplete();
                    }),
            );
        }
    }

    public ngOnDestroy(): void {
        this.formService.resetTitle();
        this.subscription.unsubscribe();
    }

    public selectNewSuggestion(position: number = 0): void {
        this.position = position;
        if (
            (!this.isBaiduEngine &&
                this.suggestions$.getValue().length === 0) ||
            this.searchInputControl.value === ""
        ) {
            this.countryError = true;
        }

        if (this.countryError || this.geolocationError) {
            this.makeTheFormInvalid();
            return;
        }

        this.makeTheFormValid();
        let currentSuggestion: AutocompletePrediction;

        if (this.isBaiduEngine) {
            /**
             * Baidu's lib writes the value directly to the  `value` attribut of the input.
             * The FormControl is not updated in this context. We have to get the value manually...
             */
            const searchLocationInput = this.document.getElementById(
                this.searchInputId,
            ) as HTMLInputElement;
            currentSuggestion = {
                mainText: "",
                secondaryText: "",
                description: searchLocationInput.value,
                placeId: "",
                types: [],
                terms: [],
            };
        } else {
            currentSuggestion = this.suggestions$.getValue()[this.position];

            /**
             * Reset the suggestion list to prevent the user from selecting another item.
             */
            this.showSuggestions$.next(false);
        }
        /**
         * Fill the search bar with the selected address
         */
        this.searchInputControl.setValue(currentSuggestion.description, {
            /**
             * At this time, no need to refresh the suggestion list because the suggestion has been approved by the user.
             * So we temporarily stop the values emitted by `this.searchInputControl.valueChanges`.
             */
            emitEvent: false,
        });
        this.changeDetectorRef.detectChanges();
        this.subscription.add(
            this.geoService
                .reverseGeocode(
                    this.isBaiduEngine
                        ? currentSuggestion.description
                        : currentSuggestion.placeId,
                    this.isBaiduEngine ? "plain" : "placeId",
                )
                .subscribe((result) => {
                    this.ngZone.run(() => {
                        this.locationSelected.emit({
                            coordinates: result.coordinates,
                            address: currentSuggestion.description,
                            isCountry:
                                currentSuggestion.types.includes("country"),
                            bounds: result.bounds,
                        });
                    });
                }),
        );
    }

    public handleKeyDown(keyboardEvent: KeyboardEvent): void {
        // When `Escape` is pressed, we simply clear the input only if input is not empty
        if (
            keyboardEvent.key === "Escape" &&
            this.searchInputControl.value !== ""
        ) {
            keyboardEvent.stopPropagation();
            this.searchInputControl.setValue("");
        }
    }

    public searchLocationSubmit(): void {
        if (this.position) {
            this.selectNewSuggestion(this.position);
            return;
        }
        /**
         * If the user clicks on the search button AND a suggestion has not been selected yet :
         * we take the first proposal by default.
         */
        this.selectNewSuggestion();
    }

    /**
     * Get the address of the user with the browser geolocalisation
     */
    public getLocation(source: "user" | "auto" = "auto"): void {
        this.subscription.add(
            this.geoService
                .getCurrentAddress()
                .pipe(
                    first(),
                    filter<string>(Boolean),
                    map((address) => {
                        if (!this.isBaiduEngine) {
                            let adressToBuild = "";
                            const tabString = address.split(", ");
                            if (tabString.length >= 2) {
                                const resultFormated = tabString.slice(
                                    -2,
                                    tabString.length,
                                );
                                adressToBuild = `${resultFormated[0]}, ${resultFormated[1]}`;
                            }
                            return adressToBuild;
                        }
                        return address;
                    }),
                )
                .subscribe({
                    next: (address) => {
                        this.geolocationError = undefined;
                        if (source === "user") {
                            this.geolocationButtonSelected.emit(address);
                        }
                        this.searchInputControl.setValue(address);
                    },
                    error: (error) => {
                        // Non-blocking error : User position is not mandatory
                        this.logger.warn(
                            `Current position not available for user because of : ${error.message}`,
                        );
                        /**
                         * Note : if error code === 1, we do nothing deliberately.
                         *
                         * @see https://developer.mozilla.org/en-US/docs/Web/API/GeolocationPositionError
                         **/
                        if (error.code === 2 || error.code === 3) {
                            this.geolocationError = error;
                            this.makeTheFormInvalid();
                        }
                    },
                }),
        );
    }

    public getMainTextFirstPart(text: string, position?: number): string {
        return position ? text.slice(0, position) : text;
    }

    public getMainTextSecondPart(text: string, position?: number): string {
        return position ? text.slice(position) : text;
    }

    /**
     * Because the validity of the input depends on conditions external to the value of the input itself,
     * its validity is managed in a non-dynmatical way.
     */
    private makeTheFormInvalid() {
        this.searchInputControl.markAsTouched();
        this.searchInputControl.setErrors({ invalid: true });
        this.formService.prefixTitleOnError();
        this.changeDetectorRef.detectChanges();
        this.formService.focusOnError(".search-location-input", {
            behavior: "smooth",
            block: "center",
        });
    }

    private makeTheFormValid() {
        this.formService.resetTitle();
        this.countryError = false;
        this.geolocationError = undefined;
        // eslint-disable-next-line unicorn/no-null
        this.searchInputControl.setErrors(null);
        this.changeDetectorRef.detectChanges();
    }

    /**
     * Return a string or a string array wich the locale(s) are where we want to search suggestions
     * When we are in France we want to search Monaco's suggestions too
     */
    private getGoogleRestrictions():
        | google.maps.places.ComponentRestrictions
        | undefined {
        if (this.isWorldwideResearch) {
            return undefined;
        }
        return {
            country: isFrance(this.locale)
                ? ["fr", "mc"]
                : this.locale.countryCode,
        };
    }

    /**
     * init baidu search
     */
    private initBaiduAutocomplete(): void {
        const searchLocationInput = this.document.getElementById(
            this.searchInputId,
        ) as HTMLInputElement;
        this.bMapAutocomplete =
            this.autoCompleteProvider.initBaiduAutocomplete(
                searchLocationInput,
            );

        /**
         * When clicking on an item in the Baidu drop-down list, an event is emitted.
         * We launch the submit action.
         */
        this.bMapAutocomplete.onconfirm = () => {
            this.selectNewSuggestion();
        };
    }

    private statusManagement(): void {
        this.subscription.add(
            this.loadGeoLibrariesService
                .autoLoadAndGetStatus()
                .subscribe((enable) => {
                    if (!enable) {
                        this.searchLocationForm.disable();
                        return;
                    }
                    this.searchLocationForm.enable();

                    this.getLocation();
                }),
        );
    }

    private searchInputChangesManagement(): void {
        // On each new value typed, the current suggestion should be reset : we expect to have new ones
        this.subscription.add(
            this.searchInputControl.valueChanges.subscribe(
                () => (this.position = undefined),
            ),
        );

        if (this.isBaiduEngine) {
            this.subscription.add(
                this.searchInputControl.valueChanges
                    .pipe(filter(() => this.countryError))
                    .subscribe(() => {
                        this.makeTheFormValid();
                    }),
            );
            return;
        }
        this.subscription.add(
            this.searchInputControl.valueChanges
                .pipe(
                    // In Angular 13 / RxJS 7, debounceTime has an issue : https://github.com/angular/angular/issues/44351
                    debounce(() => interval(200)),
                    distinctUntilChanged(),
                    switchMap((searchTerm) => {
                        if (!searchTerm) {
                            return of([]);
                        }
                        return this.autoCompleteProvider.getPlacePrediction(
                            searchTerm,
                            this.getGoogleRestrictions(),
                        );
                    }),
                )
                .subscribe((suggestions) => {
                    this.showSuggestions$.next(true);
                    this.suggestions$.next(suggestions);
                    this.changeDetectorRef.detectChanges();
                    this.searchInputComponent.focusInput();

                    /**
                     * There are new suggestions: we reset the errors.
                     */
                    if (this.countryError || this.geolocationError) {
                        this.makeTheFormValid();
                    }
                }),
        );
    }

    private unknownPlaceMessageManagement() {
        if (this.isWorldwideResearch) {
            this.unknownPlaceMessage = $localize`:@@hermes.common.no-result-found:No result found. We invite you to perform a new search.`;
            return;
        }
        /**
         * Generate the error message according the current country.
         *
         * We prefer to use the translation key `hermes_country_selector`
         * to get the exact country translation for that specific local.
         */
        this.subscription.add(
            this.translateService
                .get("hermes_country_selector")
                .subscribe((country: string) => {
                    this.unknownPlaceMessage = $localize`:@@hermes.product_locator.find-in-store-error-address-out-of-country.global:Oops!! Please enter an address in ${country}:INTERPOLATION:`;
                }),
        );
    }
}
